import { useState, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  ArrowRight,
  User,
  Linkedin,
  Check,
  CheckCircle,
  Landmark,
  Briefcase,
  Building2,
  Network,
  MapPin,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore, type InvestorType } from '@/lib/state/profile-store';
import { OnboardingLayout, INVESTOR_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import { progressFor } from '@/lib/onboarding/machine';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { capitalizeWords } from '@/lib/format';
import { buildUserProfilePrefill } from '@/lib/onboarding/user-profile-prefill';
import AnimatedPressable from '@/components/AnimatedPressable';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { track } from '@/lib/analytics';
import { useEntrance } from '@/lib/a11y/reduced-motion';

interface InvestorTypeOption {
  value: InvestorType;
  label: string;
  desc: string;
  Icon: typeof Landmark;
}

const INVESTOR_TYPES: InvestorTypeOption[] = [
  { value: 'angel', label: 'Angel', desc: 'Investing your own capital', Icon: User },
  { value: 'vc', label: 'Venture Capital', desc: 'Partner / team at a fund', Icon: Briefcase },
  { value: 'family_office', label: 'Family Office', desc: 'Managing family capital', Icon: Landmark },
  { value: 'corporate', label: 'Corporate / CVC', desc: 'Strategic investor', Icon: Building2 },
  { value: 'syndicate', label: 'Syndicate', desc: 'Lead an SPV / syndicate', Icon: Network },
];

// Curated hubs for the location autocomplete — free text is always accepted too,
// so this is a convenience filter, not a closed set (no silent defaulting).
const LOCATION_SUGGESTIONS: string[] = [
  'Zurich', 'Geneva', 'Lausanne', 'Basel', 'Zug', 'Bern',
  'London', 'Berlin', 'Paris', 'Amsterdam', 'Stockholm', 'Munich',
  'New York', 'San Francisco', 'Boston', 'Singapore', 'Dubai',
];

export default function InvestorIdentityScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const entrance = useEntrance();
  const { isDesktop } = useResponsive();
  const { show } = useAlert();

  const user = useProfileStore((s) => s.user);
  const setUser = useProfileStore((s) => s.setUser);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);

  const { user: authUser, profile: authProfile, refreshProfile } = useAuth();

  // Pure prefill: previously-confirmed profile name wins over raw LinkedIn name.
  const prefill = useMemo(
    () => buildUserProfilePrefill(authProfile, authUser),
    [authProfile, authUser],
  );

  const [firstName, setFirstName] = useState(prefill.firstName);
  const [lastName, setLastName] = useState(prefill.lastName);
  const [investorType, setInvestorType] = useState<InvestorType | null>(
    investorProfile?.investorType ?? null,
  );
  const [firm, setFirm] = useState(investorProfile?.firm || prefill.currentCompany || '');
  const [role, setRole] = useState(investorProfile?.role || prefill.headline || '');
  const [location, setLocation] = useState(investorProfile?.location || '');
  const [locationFocused, setLocationFocused] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const avatarUrl = prefill.avatarUrl ?? user?.avatarUrl ?? null;
  const email = prefill.email;
  const linkedInVerified = authProfile?.linkedin_verified === true;

  const requiresFirm = investorType !== null && investorType !== 'angel';

  const locationMatches = useMemo(() => {
    const q = location.trim().toLowerCase();
    if (!q) return [];
    return LOCATION_SUGGESTIONS.filter(
      (c) => c.toLowerCase().includes(q) && c.toLowerCase() !== q,
    ).slice(0, 5);
  }, [location]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = 'First name is required';
    if (!lastName.trim()) next.lastName = 'Last name is required';
    if (!investorType) next.investorType = 'Select your investor type';
    if (requiresFirm && !firm.trim()) next.firm = 'Firm / fund name is required';
    if (!role.trim()) next.role = 'Your role is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContinue = async () => {
    if (isSaving) return;
    if (!validate()) return;

    setSaveError(null);
    setIsSaving(true);

    const finalFirst = capitalizeWords(firstName.trim());
    const finalLast = capitalizeWords(lastName.trim());
    const fullName = [finalFirst, finalLast].filter(Boolean).join(' ');
    const trimmedFirm = requiresFirm ? firm.trim() : firm.trim() || undefined;

    // Local (Zustand) state first so the rest of the app has it immediately.
    setUser?.({
      ...user,
      name: fullName,
      email,
      role: 'investor',
      avatarUrl: avatarUrl || undefined,
    });
    setInvestorProfile?.({
      id: investorProfile?.id || `investor-${Date.now()}`,
      firm: trimmedFirm || undefined,
      investorType: investorType ?? undefined,
      role: role.trim() || undefined,
      location: location.trim(),
      investmentFocus: investorProfile?.investmentFocus || [],
      stagePreference: investorProfile?.stagePreference || [],
      geographies: investorProfile?.geographies,
      ticketSize: investorProfile?.ticketSize || { min: 0, max: 0 },
      ticketCurrency: investorProfile?.ticketCurrency,
      leadPreference: investorProfile?.leadPreference,
      portfolioCount: investorProfile?.portfolioCount || 0,
      linkedInVerified: linkedInVerified || investorProfile?.linkedInVerified || false,
      investmentHistory: investorProfile?.investmentHistory || [],
      portfolioClaims: investorProfile?.portfolioClaims || [],
    });

    // Real persistence — AWAITED before navigation. On failure we surface an
    // inline retry and do NOT advance (no fire-and-forget, no simulated success).
    if (isSupabaseConfigured() && authUser?.id) {
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ first_name: finalFirst || null, last_name: finalLast || null })
          .eq('id', authUser.id);
        if (profileError) throw profileError;

        const investorPayload = {
          user_id: authUser.id,
          name: fullName || email,
          firm: trimmedFirm || null,
          investor_type: investorType,
          headline: role.trim() || null,
          location: location.trim() || null,
        };
        const { error: investorError } = await supabase
          .from('investor_profiles')
          .upsert(investorPayload, { onConflict: 'user_id' });
        if (investorError) throw investorError;

        // Resume pointer (best-effort, non-blocking): land on thesis next time.
        await supabase
          .from('profiles')
          .update({ onboarding_step: 'thesis' })
          .eq('id', authUser.id);

        await refreshProfile();
      } catch (err) {
        setIsSaving(false);
        const { message, isCancelled } = handleError(err, {
          context: 'InvestorIdentity.handleContinue',
        });
        if (!isCancelled) {
          setSaveError(message);
          show({ title: "Couldn't save your profile", message, buttons: [{ text: 'OK' }] });
        }
        return;
      }
    }

    // Activation §8 — investor identity captured, after the awaited profile +
    // investor_profiles writes above (a throw skips to catch, never here).
    track('investor_identity_done', { investorType: investorType ?? 'unset' });

    setIsSaving(false);
    router.push('/(onboarding)/investor-thesis');
  };

  const inputStyle = (hasError?: boolean) => ({
    backgroundColor: colors.surface.primary,
    borderWidth: 1,
    borderColor: hasError ? colors.red.main : colors.border.light,
    borderRadius: 12,
    padding: 16,
    color: colors.text.primary,
    fontSize: 16,
  });

  const formContent = (
    <>
      <View className={isWeb && isDesktop ? 'gap-5' : 'gap-4'}>
        {/* Avatar from LinkedIn */}
        {avatarUrl && (
          <View className="items-center mb-2">
            <View
              className="w-20 h-20 rounded-full overflow-hidden"
              style={{ borderWidth: 3, borderColor: colors.primary[500] }}
            >
              <Image source={{ uri: avatarUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
            </View>
          </View>
        )}

        {/* Name row */}
        <View className="flex-row gap-3">
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              First name <Text style={{ color: colors.red.main }}>*</Text>
            </Text>
            <TextInput
              style={inputStyle(!!errors.firstName)}
              placeholder="First name"
              placeholderTextColor={colors.text.tertiary}
              value={firstName}
              onChangeText={(t) => { setFirstName(t); if (errors.firstName) setErrors((e) => ({ ...e, firstName: '' })); }}
              autoCapitalize="words"
              accessibilityLabel="First name"
            />
            {!!errors.firstName && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.firstName}</Text>}
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              Last name <Text style={{ color: colors.red.main }}>*</Text>
            </Text>
            <TextInput
              style={inputStyle(!!errors.lastName)}
              placeholder="Last name"
              placeholderTextColor={colors.text.tertiary}
              value={lastName}
              onChangeText={(t) => { setLastName(t); if (errors.lastName) setErrors((e) => ({ ...e, lastName: '' })); }}
              autoCapitalize="words"
              accessibilityLabel="Last name"
            />
            {!!errors.lastName && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.lastName}</Text>}
          </View>
        </View>

        {/* Email — read-only (login identity) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">Email</Text>
          <View
            className="flex-row items-center gap-2"
            style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light, borderRadius: 12, padding: 16 }}
          >
            <Text style={{ color: colors.text.secondary, fontSize: 16, flex: 1 }} numberOfLines={1}>
              {email || 'Your login email'}
            </Text>
            <CheckCircle size={16} color={colors.traffic.green} />
          </View>
        </View>

        {/* Investor type — radio cards */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Investor type <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="gap-2">
            {INVESTOR_TYPES.map((opt) => {
              const selected = investorType === opt.value;
              const OptIcon = opt.Icon;
              return (
                <View key={opt.value} style={{ borderRadius: 14, overflow: 'hidden' }}>
                  <Pressable
                    onPress={() => { setInvestorType(opt.value); if (errors.investorType) setErrors((e) => ({ ...e, investorType: '' })); }}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    accessibilityLabel={`${opt.label}. ${opt.desc}`}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      minHeight: 60,
                      paddingHorizontal: 14,
                      paddingVertical: 12,
                      backgroundColor: selected ? colors.primary[50] : colors.surface.primary,
                      borderWidth: 1.5,
                      borderColor: selected ? colors.primary[500] : colors.border.light,
                      borderRadius: 14,
                    }}
                  >
                    <View
                      className="w-10 h-10 rounded-xl items-center justify-center"
                      style={{ backgroundColor: selected ? colors.primary[100] : colors.surface.secondary }}
                    >
                      <OptIcon size={20} color={selected ? colors.primary[600] : colors.text.tertiary} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: selected ? colors.primary[700] : colors.text.primary }} className="text-base font-semibold" numberOfLines={1}>
                        {opt.label}
                      </Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{opt.desc}</Text>
                    </View>
                    {selected && (
                      <View className="w-6 h-6 rounded-full items-center justify-center" style={{ backgroundColor: colors.primary[600] }}>
                        <Check size={14} color="white" />
                      </View>
                    )}
                  </Pressable>
                </View>
              );
            })}
          </View>
          {!!errors.investorType && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.investorType}</Text>}
        </View>

        {/* Firm — only when type != angel */}
        {requiresFirm && (
          <View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              Firm / fund <Text style={{ color: colors.red.main }}>*</Text>
            </Text>
            <TextInput
              style={inputStyle(!!errors.firm)}
              placeholder="e.g., Founders Fund"
              placeholderTextColor={colors.text.tertiary}
              value={firm}
              onChangeText={(t) => { setFirm(t); if (errors.firm) setErrors((e) => ({ ...e, firm: '' })); }}
              accessibilityLabel="Firm or fund name"
            />
            {!!errors.firm && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.firm}</Text>}
          </View>
        )}

        {/* Functional role */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Your role <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <TextInput
            style={inputStyle(!!errors.role)}
            placeholder="e.g., Partner, Managing Director, Angel"
            placeholderTextColor={colors.text.tertiary}
            value={role}
            onChangeText={(t) => { setRole(t); if (errors.role) setErrors((e) => ({ ...e, role: '' })); }}
            accessibilityLabel="Your functional role"
          />
          {!!errors.role && <Text style={{ color: colors.red.main }} className="text-xs mt-1">{errors.role}</Text>}
        </View>

        {/* Location — autocomplete (free text accepted) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">Location</Text>
          <View className="flex-row items-center gap-2" style={inputStyle(false)}>
            <MapPin size={18} color={colors.text.tertiary} />
            <TextInput
              style={{ flex: 1, color: colors.text.primary, fontSize: 16, padding: 0 }}
              placeholder="City (optional)"
              placeholderTextColor={colors.text.tertiary}
              value={location}
              onChangeText={setLocation}
              onFocus={() => setLocationFocused(true)}
              onBlur={() => setLocationFocused(false)}
              accessibilityLabel="Location"
            />
          </View>
          {locationFocused && locationMatches.length > 0 && (
            <View
              className="mt-1 rounded-xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
            >
              {locationMatches.map((city, i) => (
                <Pressable
                  key={`loc-${city}`}
                  onPress={() => { setLocation(city); setLocationFocused(false); }}
                  accessibilityRole="button"
                  accessibilityLabel={`Use ${city}`}
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderTopWidth: i === 0 ? 0 : 1,
                    borderTopColor: colors.border.light,
                  }}
                >
                  <Text style={{ color: colors.text.primary }} className="text-sm">{city}</Text>
                </Pressable>
              ))}
            </View>
          )}
        </View>

        {/* LinkedIn (optional) */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">LinkedIn</Text>
          {linkedInVerified ? (
            <View
              className="flex-row items-center justify-center gap-2 py-3 rounded-xl"
              style={{ backgroundColor: colors.traffic.greenLight, borderWidth: 1, borderColor: colors.traffic.green }}
            >
              <CheckCircle size={18} color={colors.traffic.green} />
              <Text style={{ color: colors.traffic.green }} className="font-medium text-sm">LinkedIn verified</Text>
            </View>
          ) : (
            <AnimatedPressable
              onPress={() => router.push('/(onboarding)/linkedin-verify')}
              accessibilityRole="button"
              accessibilityLabel="Verify with LinkedIn (optional)"
              className="flex-row items-center justify-center gap-2 py-3 rounded-xl"
              style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
            >
              <Linkedin size={18} color="#0A66C2" />
              <Text style={{ color: '#0A66C2' }} className="font-medium text-sm">Verify with LinkedIn (optional)</Text>
            </AnimatedPressable>
          )}
        </View>
      </View>

      {!!saveError && (
        <View className="mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.red.light, borderWidth: 1, borderColor: colors.red.main }}>
          <Text style={{ color: colors.red.main }} className="text-sm">{saveError}</Text>
        </View>
      )}

      {/* Continue */}
      <View className="mt-8">
        <View style={{ borderRadius: 12, overflow: 'hidden', backgroundColor: isSaving ? colors.border.medium : colors.primary[600] }}>
          <Pressable
            onPress={handleContinue}
            disabled={isSaving}
            accessibilityRole="button"
            accessibilityLabel="Continue to investment thesis"
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 16 }}
          >
            <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
              {isSaving ? 'Saving…' : 'Continue'}
            </Text>
            {!isSaving && <ArrowRight size={20} color={colors.text.inverse} />}
          </Pressable>
        </View>
      </View>
    </>
  );

  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="identity"
        steps={INVESTOR_ONBOARDING_STEPS}
        title="Your Profile"
        subtitle="Tell us who you are and how you invest"
        userType="investor"
        entityName={[firstName, lastName].filter(Boolean).join(' ') || undefined}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  const { index, total } = progressFor('investor', 'identity');

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} className="flex-1">
          <Animated.View entering={entrance(FadeInDown.delay(100).duration(500))} className="px-5 pt-4 pb-4">
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel="Go back"
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                {Array.from({ length: total }).map((_, i) => (
                  <View
                    key={`dot-${i}`}
                    className="h-1 w-8 rounded-full"
                    style={{ backgroundColor: i <= index ? colors.primary[500] : colors.border.light }}
                  />
                ))}
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          <Animated.View entering={entrance(FadeInDown.delay(200).duration(500))} className="px-5 pb-4">
            <View className="flex-row items-center gap-3 mb-2">
              <User size={22} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step {index + 1} of {total}
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">Your Profile</Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Tell us who you are and how you invest
            </Text>
          </Animated.View>

          <ScrollView className="flex-1 px-5" showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <Animated.View entering={entrance(FadeInUp.delay(300).duration(500))} className="pb-10">
              {formContent}
            </Animated.View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
